import { useEffect, useMemo, useState } from 'react';
import { GripVertical, Loader2, Plus, Timer, Trash2, Minus } from 'lucide-react';
import { api } from '../lib/spizirnaUi';
import Dropdown from './receptura/Dropdown';
import ModalShell from './ui/ModalShell';
import usePotvrzeni from './ui/usePotvrzeni';
import useJeMobil from '../hooks/useJeMobil';

const TONY = [
    { value: 'zvonek', label: 'Zvonek' },
    { value: 'gong', label: 'Gong' },
    { value: 'pipani', label: 'Pípání' },
];

const VELIKOSTI = [
    { id: 'auto', nazev: 'Auto' },
    { id: 'kompaktni', nazev: 'Kompaktní' },
    { id: 'standard', nazev: 'Standard' },
    { id: 'velka', nazev: 'Velká' },
];

/** Co znamená „Auto" pro dané rozlišení — musí sedět s POS (lib/minutky.js). */
export function autoVelikost(obrazovka) {
    const sirka = parseInt(String(obrazovka || '').split('x')[0], 10);
    if (!sirka) return null;
    if (sirka < 1600) return 'kompaktni';
    if (sirka <= 2560) return 'standard';
    return 'velka';
}

export const cas = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

const novyKrok = () => ({ nazev: '', akce: '', s: 240, konec: 'tuk', po_s: 5 });

/**
 * Správa → Provozovna → Minutky (2026-10-24).
 *
 * Předvolby jsou dlaždice v liště kuchyně: jedno ťuknutí = minutka běží.
 * Patří stanicím (Bistro, Pizza, Bar); pokladna dostane jen předvolby své
 * kuchyně. Zapnout minutky jde u každé pokladny (Pokladny a telefon) i tady
 * v záložce Displeje, kde se nastavuje i velikost lišty.
 */
export default function MinutkySprava({ isDark, barva = '#3B82F6' }) {
    const isMobile = useJeMobil();
    const [potvrdit, potvrzeni] = usePotvrzeni(isDark);
    const [zalozka, setZalozka] = useState('predvolby');
    const [predvolby, setPredvolby] = useState(null);
    const [stanice, setStanice] = useState([]);
    const [terminaly, setTerminaly] = useState([]);
    const [filtr, setFiltr] = useState(null);
    const [upravovana, setUpravovana] = useState(null);
    const [tazena, setTazena] = useState(null);
    const [chyba, setChyba] = useState('');

    const nacti = () => {
        api('/api/kds-minutky').then(setPredvolby).catch(() => setPredvolby([]));
        api('/api/pos-terminaly').then(d => setTerminaly((d.terminaly || []).filter(t => t.typ === 'pokladna' && t.aktivni))).catch(() => {});
    };
    useEffect(() => {
        nacti();
        api('/api/kds-stanice').then(s => setStanice(s.filter(x => x.aktivni))).catch(() => {});
    }, []);

    const muted = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const border = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const kartaBg = isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-base)';

    const proved = async (fn) => {
        setChyba('');
        try { await fn(); } catch (e) { setChyba(e?.error || e?.message || 'Nepodařilo se uložit.'); }
        nacti();
    };

    const hlavni = stanice.find(s => s.druh === 'kuchyne' && s.cislo === 1);
    const staniceTerminalu = (t) => t.kds_stanice_id ?? hlavni?.id;
    const nazevStanice = (id) => stanice.find(s => s.id === id)?.nazev ?? '—';
    const barvaStanice = (id) => stanice.find(s => s.id === id)?.barva || muted;

    const viditelne = useMemo(
        () => (predvolby || []).filter(p => !filtr || p.stanice.includes(filtr)),
        [predvolby, filtr]
    );

    /** Pořadí v liště u dané stanice (jen připnuté a aktivní). */
    const poradiVListe = (p, staniceId) => {
        const pripnute = (predvolby || []).filter(x => x.aktivni && x.v_liste && x.stanice.includes(staniceId));
        const i = pripnute.findIndex(x => x.id === p.id);
        return i >= 0 ? i + 1 : null;
    };

    const pustit = (cil) => {
        if (!tazena || tazena === cil) return;
        const ids = (predvolby || []).map(p => p.id).filter(id => id !== tazena);
        ids.splice(ids.indexOf(cil), 0, tazena);
        setPredvolby(prev => ids.map(id => prev.find(p => p.id === id)));
        proved(() => api('/api/kds-minutky/poradi', 'PUT', { ids }));
        setTazena(null);
    };

    if (!predvolby) return <Loader2 size={18} className="animate-spin" style={{ color: muted }} />;

    return (
        <div className="space-y-4 max-w-3xl">
            {potvrzeni}
            <div>
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Minutky</h3>
                <p className="text-xs mt-1" style={{ color: muted }}>
                    Předvolby pro kuchyňské displeje. Co se vejde do lišty (Full HD 4–5, HD 3), je na jedno ťuknutí, zbytek je pod tlačítkem minutky.
                    Zapínají se u každé pokladny — tady v záložce Displeje nebo v Pokladny a telefon.
                </p>
            </div>

            <div className="flex gap-1" style={{ borderBottom: `1px solid ${border}` }}>
                {[{ id: 'predvolby', nazev: 'Předvolby' }, { id: 'displeje', nazev: 'Displeje a velikost' }].map(z => (
                    <button key={z.id} type="button" onClick={() => setZalozka(z.id)} className="text-xs px-3 py-2"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: zalozka === z.id ? 700 : 400,
                            color: zalozka === z.id ? 'var(--text-primary)' : muted, borderBottom: `2px solid ${zalozka === z.id ? barva : 'transparent'}`, minHeight: 36 }}>
                        {z.nazev}
                    </button>
                ))}
            </div>

            {chyba && <p className="text-xs" style={{ color: '#EF4444' }}>{chyba}</p>}

            {zalozka === 'predvolby' && (
                <>
                    <div className="flex flex-wrap items-center gap-2">
                        <FiltrStanice aktivni={!filtr} onClick={() => setFiltr(null)} border={border} muted={muted}>Všechny · {predvolby.length}</FiltrStanice>
                        {stanice.map(s => (
                            <FiltrStanice key={s.id} aktivni={filtr === s.id} onClick={() => setFiltr(s.id)} border={border} muted={muted} tecka={s.barva}>
                                {s.nazev} · {predvolby.filter(p => p.stanice.includes(s.id)).length}
                            </FiltrStanice>
                        ))}
                        <button type="button" onClick={() => setUpravovana({ nazev: '', kroky: [novyKrok()], ton: 'zvonek', v_liste: true, aktivni: true, stanice: filtr ? [filtr] : (hlavni ? [hlavni.id] : []), skryto_terminaly: [] })}
                            className="ml-auto flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-2"
                            style={{ background: barva, border: 'none', color: '#fff', cursor: 'pointer', minHeight: isMobile ? 44 : 34 }}>
                            <Plus size={13} /> Nová předvolba
                        </button>
                    </div>

                    {viditelne.length === 0 ? (
                        <p className="text-xs" style={{ color: muted }}>Zatím žádná předvolba. Začni třeba „Maso 4:00 → Otočit 3:00“.</p>
                    ) : (
                        <div className="space-y-1.5">
                            {viditelne.map(p => (
                                <div key={p.id}
                                    draggable={!isMobile && !filtr}
                                    onDragStart={() => setTazena(p.id)}
                                    onDragOver={e => { if (tazena) e.preventDefault(); }}
                                    onDrop={() => pustit(p.id)}
                                    onClick={() => setUpravovana(p)}
                                    className="flex flex-wrap items-center gap-3 rounded-xl px-3 py-2.5"
                                    style={{ background: kartaBg, border: `1px solid ${border}`, opacity: p.aktivni ? (tazena === p.id ? 0.4 : 1) : 0.5, cursor: 'pointer' }}>
                                    {!isMobile && !filtr && <GripVertical size={15} style={{ color: muted, cursor: 'grab' }} />}
                                    <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)', minWidth: 110 }}>{p.nazev}</span>
                                    <span className="text-sm font-bold tabular-nums" style={{ color: 'var(--text-primary)', minWidth: 48 }}>{cas(p.kroky[0]?.s ?? 0)}</span>
                                    <span className="text-xs flex-1 min-w-0 truncate" style={{ color: muted }}>
                                        {p.kroky.map((k, i) => `${i ? `${k.nazev || 'krok'} ${cas(k.s)} · ` : ''}${k.akce || (k.konec === 'samo' ? 'zhasne samo' : 'zvoní')}`).join(' → ')}
                                    </span>
                                    <span className="flex flex-wrap gap-1">
                                        {p.stanice.map(id => (
                                            <span key={id} className="text-[11px] font-semibold rounded-full px-2 py-0.5"
                                                style={{ background: `${barvaStanice(id)}22`, color: 'var(--text-primary)', border: `1px solid ${barvaStanice(id)}66` }}>
                                                {nazevStanice(id)}
                                            </span>
                                        ))}
                                    </span>
                                    <span className="text-[11px] font-bold" style={{ color: p.v_liste ? barva : muted, minWidth: 56, textAlign: 'right' }}>
                                        {!p.aktivni ? 'vypnutá' : p.v_liste ? (filtr ? `${poradiVListe(p, filtr) ?? '—'}. v liště` : 'v liště') : 'pod ⏱'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                    {!isMobile && !filtr && viditelne.length > 1 && <p className="text-[11px]" style={{ color: muted }}>Pořadí se mění tažením. V liště je to, co se vejde, od shora.</p>}
                </>
            )}

            {zalozka === 'displeje' && (
                <Displeje terminaly={terminaly} stanice={stanice} staniceTerminalu={staniceTerminalu} nazevStanice={nazevStanice} barvaStanice={barvaStanice}
                    isDark={isDark} barva={barva} muted={muted} border={border} kartaBg={kartaBg}
                    onZmena={(t, zmena) => {
                        setTerminaly(prev => prev.map(x => x.id === t.id ? { ...x, ...zmena } : x));
                        proved(() => api(`/api/pos-terminaly/${t.id}`, 'PUT', zmena));
                    }} />
            )}

            {upravovana && (
                <EditorPredvolby predvolba={upravovana} stanice={stanice} terminaly={terminaly} staniceTerminalu={staniceTerminalu}
                    isDark={isDark} barva={barva} muted={muted} border={border}
                    onZavrit={() => setUpravovana(null)}
                    onUlozit={async (data) => {
                        setChyba('');
                        try {
                            if (data.id) await api(`/api/kds-minutky/${data.id}`, 'PUT', data);
                            else await api('/api/kds-minutky', 'POST', data);
                            setUpravovana(null);
                        } catch (e) {
                            setChyba(e?.error || e?.message || 'Nepodařilo se uložit.');
                        }
                        nacti();
                    }}
                    onOdstranit={upravovana.id ? async () => {
                        if (await potvrdit(`Smazat předvolbu „${upravovana.nazev}“?`, { potvrdit: 'Smazat' })) {
                            setUpravovana(null);
                            proved(() => api(`/api/kds-minutky/${upravovana.id}`, 'DELETE'));
                        }
                    } : null} />
            )}
        </div>
    );
}

function FiltrStanice({ aktivni, onClick, children, border, muted, tecka }) {
    return (
        <button type="button" onClick={onClick} className="flex items-center gap-1.5 text-xs rounded-full px-3"
            style={{ minHeight: 32, cursor: 'pointer', fontWeight: aktivni ? 700 : 400, color: aktivni ? 'var(--text-primary)' : muted,
                background: aktivni ? 'rgba(127,127,127,0.16)' : 'none', border: `1px solid ${aktivni ? 'transparent' : border}` }}>
            {tecka && <span style={{ width: 8, height: 8, borderRadius: 4, background: tecka }} />}
            {children}
        </button>
    );
}

/** Úprava jedné předvolby — název, kroky (jak každý skončí), tón, kde se ukáže. */
function EditorPredvolby({ predvolba, stanice, terminaly, staniceTerminalu, isDark, barva, muted, border, onZavrit, onUlozit, onOdstranit }) {
    const [p, setP] = useState(() => ({ ...predvolba, kroky: predvolba.kroky.map(k => ({ po_s: 5, ...k })) }));
    const [uklada, setUklada] = useState(false);
    const fieldBg = isDark ? 'rgba(255,255,255,0.05)' : '#fff';
    const pole = { background: fieldBg, border: `1px solid ${border}`, color: 'var(--text-primary)', borderRadius: 10, minHeight: 40, padding: '0 10px', fontSize: 14 };

    const zmenKrok = (i, zmena) => setP(x => ({ ...x, kroky: x.kroky.map((k, j) => j === i ? { ...k, ...zmena } : k) }));
    const krokS = (i, delta) => zmenKrok(i, { s: Math.max(5, Math.min(14400, p.kroky[i].s + delta)) });
    const prepniStanici = (id) => setP(x => ({ ...x, stanice: x.stanice.includes(id) ? x.stanice.filter(s => s !== id) : [...x.stanice, id] }));
    const prepniSkryti = (id) => setP(x => {
        const sk = x.skryto_terminaly || [];
        return { ...x, skryto_terminaly: sk.includes(id) ? sk.filter(s => s !== id) : [...sk, id] };
    });
    const displeje = terminaly.filter(t => p.stanice.includes(staniceTerminalu(t)));
    const lzeUlozit = p.nazev.trim() && p.kroky.length > 0 && p.stanice.length > 0 && !uklada;

    const ulozit = async () => {
        setUklada(true);
        await onUlozit({
            id: p.id, nazev: p.nazev.trim(), ton: p.ton, v_liste: p.v_liste, aktivni: p.aktivni,
            stanice: p.stanice, skryto_terminaly: p.skryto_terminaly || [],
            kroky: p.kroky.map(k => ({ nazev: k.nazev, akce: k.akce ?? '', s: k.s, konec: k.konec, po_s: k.konec === 'samo' ? k.po_s : null })),
        });
        setUklada(false);
    };

    return (
        <ModalShell onClose={onZavrit} isDark={isDark} nadpis={p.id ? 'Upravit předvolbu' : 'Nová předvolba'} ikona={Timer} barva={barva} sirka={560}
            paticka={
                <div className="flex items-center gap-2 w-full">
                    {onOdstranit && (
                        <button type="button" onClick={onOdstranit} className="flex items-center gap-1.5 text-xs font-semibold"
                            style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', minHeight: 40 }}>
                            <Trash2 size={14} /> Smazat
                        </button>
                    )}
                    <span className="flex-1" />
                    <button type="button" onClick={onZavrit} className="text-sm rounded-lg px-4"
                        style={{ minHeight: 40, background: 'none', border: `1px solid ${border}`, color: 'var(--text-primary)', cursor: 'pointer' }}>Zrušit</button>
                    <button type="button" onClick={ulozit} disabled={!lzeUlozit} className="text-sm font-semibold rounded-lg px-4"
                        style={{ minHeight: 40, background: barva, border: 'none', color: '#fff', cursor: lzeUlozit ? 'pointer' : 'default', opacity: lzeUlozit ? 1 : 0.5 }}>Uložit</button>
                </div>
            }>
            <div className="space-y-4">
                <label className="block">
                    <span className="block text-xs mb-1" style={{ color: muted }}>Název na dlaždici</span>
                    <input autoFocus value={p.nazev} maxLength={40} onChange={e => setP(x => ({ ...x, nazev: e.target.value }))} placeholder="Maso" className="w-full font-semibold" style={pole} />
                </label>

                <div>
                    <span className="block text-xs mb-1.5" style={{ color: muted }}>Kroky</span>
                    <div className="space-y-2">
                        {p.kroky.map((k, i) => (
                            <div key={i} className="rounded-xl p-2.5 space-y-2" style={{ border: `1px solid ${border}` }}>
                                <div className="flex items-center gap-2">
                                    <span className="text-[11px] font-bold rounded-full flex items-center justify-center" style={{ width: 22, height: 22, background: `${barva}22`, color: barva, flexShrink: 0 }}>{i + 1}</span>
                                    <input value={k.nazev} maxLength={30} onChange={e => zmenKrok(i, { nazev: e.target.value })}
                                        placeholder="když běží, třeba Pečení" aria-label={`Text kroku ${i + 1}, když běží`} className="flex-1 min-w-0" style={pole} />
                                    <div className="flex items-center" style={{ ...pole, padding: 0, overflow: 'hidden' }}>
                                        <button type="button" aria-label="Ubrat" onClick={() => krokS(i, k.s <= 60 ? -5 : -30)} style={{ width: 36, height: 40, background: 'none', border: 'none', color: muted, cursor: 'pointer' }}><Minus size={14} /></button>
                                        <span className="text-sm font-bold tabular-nums" style={{ minWidth: 52, textAlign: 'center' }}>{cas(k.s)}</span>
                                        <button type="button" aria-label="Přidat" onClick={() => krokS(i, k.s < 60 ? 5 : 30)} style={{ width: 36, height: 40, background: 'none', border: 'none', color: muted, cursor: 'pointer' }}><Plus size={14} /></button>
                                    </div>
                                    {p.kroky.length > 1 && (
                                        <button type="button" aria-label="Odebrat krok" onClick={() => setP(x => ({ ...x, kroky: x.kroky.filter((_, j) => j !== i) }))}
                                            style={{ width: 32, height: 40, background: 'none', border: 'none', color: muted, cursor: 'pointer' }}><Trash2 size={14} /></button>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 pl-7 text-xs" style={{ color: muted }}>
                                    <span style={{ minWidth: 70 }}>{k.konec === 'samo' ? 'na konci' : 'když zvoní'}</span>
                                    <input value={k.akce ?? ''} maxLength={30} onChange={e => zmenKrok(i, { akce: e.target.value })}
                                        placeholder={k.konec === 'samo' ? 'třeba Okapáno' : i < p.kroky.length - 1 ? 'třeba Otočit' : 'třeba Vyndat'}
                                        aria-label={`Text kroku ${i + 1}, když zvoní`} className="flex-1 min-w-0" style={{ ...pole, minHeight: 34 }} />
                                </div>
                                <div className="flex flex-wrap items-center gap-2 pl-7 text-xs" style={{ color: muted }}>
                                    konec
                                    <div className="flex rounded-lg p-0.5" style={{ border: `1px solid ${border}` }}>
                                        {[{ id: 'tuk', nazev: 'ťuknutím' }, { id: 'samo', nazev: 'samo' }].map(o => (
                                            <button key={o.id} type="button" onClick={() => zmenKrok(i, { konec: o.id })} className="text-xs rounded-md px-2.5"
                                                style={{ minHeight: 30, border: 'none', cursor: 'pointer', fontWeight: k.konec === o.id ? 700 : 400,
                                                    background: k.konec === o.id ? barva : 'none', color: k.konec === o.id ? '#fff' : muted }}>{o.nazev}</button>
                                        ))}
                                    </div>
                                    {k.konec === 'samo' ? (
                                        <>po
                                            <input type="number" min={2} max={120} value={k.po_s} onChange={e => zmenKrok(i, { po_s: Math.max(2, Math.min(120, parseInt(e.target.value, 10) || 5)) })}
                                                aria-label="Zhasne po sekundách" style={{ ...pole, width: 64, minHeight: 32 }} /> s
                                        </>
                                    ) : <span>zvoní, dokud někdo neťukne</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                    {p.kroky.length < 4 && (
                        <button type="button" onClick={() => setP(x => ({ ...x, kroky: [...x.kroky, { ...novyKrok(), s: 180 }] }))}
                            className="mt-2 flex items-center gap-1.5 text-xs font-semibold" style={{ background: 'none', border: 'none', color: muted, cursor: 'pointer', minHeight: 32 }}>
                            <Plus size={13} /> Přidat krok
                        </button>
                    )}
                    <p className="text-[11px] mt-1" style={{ color: muted }}>Ťuknutím = zvoní, dokud někdo neťukne a nepustí další krok. Samo = pípne, chvíli svítí a zhasne.</p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-xs" style={{ color: muted }}>
                        tón
                        <Dropdown value={p.ton} isDark={isDark} height={36} minWidth={130} options={TONY} onChange={v => setP(x => ({ ...x, ton: v }))} />
                    </label>
                    <Prepinac zapnuto={p.v_liste} onClick={() => setP(x => ({ ...x, v_liste: !x.v_liste }))} barva={barva} muted={muted} isDark={isDark}>připnout do lišty</Prepinac>
                    <Prepinac zapnuto={p.aktivni} onClick={() => setP(x => ({ ...x, aktivni: !x.aktivni }))} barva={barva} muted={muted} isDark={isDark}>zapnutá</Prepinac>
                </div>

                <div>
                    <span className="block text-xs mb-1.5" style={{ color: muted }}>Kde se ukáže</span>
                    <div className="flex flex-wrap gap-2">
                        {stanice.map(s => {
                            const on = p.stanice.includes(s.id);
                            return (
                                <button key={s.id} type="button" onClick={() => prepniStanici(s.id)} className="flex items-center gap-1.5 text-sm rounded-lg px-3"
                                    style={{ minHeight: 40, cursor: 'pointer', fontWeight: on ? 700 : 400, color: on ? 'var(--text-primary)' : muted,
                                        background: on ? `${s.barva || barva}22` : 'none', border: `1.5px solid ${on ? (s.barva || barva) : border}` }}>
                                    <span style={{ width: 8, height: 8, borderRadius: 4, background: s.barva || muted }} />{s.nazev}
                                </button>
                            );
                        })}
                    </div>
                    {displeje.length > 0 && (
                        <div className="mt-2 rounded-lg" style={{ border: `1px solid ${border}` }}>
                            <p className="text-[11px] px-3 pt-2" style={{ color: muted }}>Displeje vybraných kuchyní — odškrtnutím ji schováš jen na jednom</p>
                            {displeje.map(t => (
                                <label key={t.id} className="flex items-center gap-2 text-xs px-3" style={{ minHeight: 34, color: 'var(--text-primary)' }}>
                                    <input type="checkbox" checked={!(p.skryto_terminaly || []).includes(t.id)} onChange={() => prepniSkryti(t.id)} />
                                    {t.nazev}
                                    <span style={{ color: muted }}>{t.minutky ? '' : '· minutky vypnuté'}</span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </ModalShell>
    );
}

function Prepinac({ zapnuto, onClick, children, barva, muted, isDark }) {
    return (
        <button type="button" onClick={onClick} className="flex items-center gap-1.5 text-xs"
            style={{ color: zapnuto ? 'var(--text-primary)' : muted, background: 'none', border: 'none', cursor: 'pointer', minHeight: 36 }}>
            <span style={{ width: 30, height: 18, borderRadius: 9, position: 'relative', background: zapnuto ? barva : (isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)') }}>
                <span style={{ position: 'absolute', top: 2, left: zapnuto ? 14 : 2, width: 14, height: 14, borderRadius: 7, background: '#fff' }} />
            </span>
            {children}
        </button>
    );
}

/** Pokladny: minutky zapnout/vypnout, velikost lišty, doladění a zvuk. */
function Displeje({ terminaly, staniceTerminalu, nazevStanice, barvaStanice, isDark, barva, muted, border, kartaBg, onZmena }) {
    if (!terminaly.length) return <p className="text-xs" style={{ color: muted }}>Žádná aktivní pokladna.</p>;
    return (
        <div className="space-y-2">
            {terminaly.map(t => {
                const z = t.minutky_zobrazeni || { velikost: 'auto', meritko: 100, zvuk: true };
                const auto = autoVelikost(t.obrazovka);
                const uloz = (zmena) => onZmena(t, { minutky_zobrazeni: { ...z, ...zmena } });
                return (
                    <div key={t.id} className="rounded-xl px-4 py-3 space-y-2.5" style={{ background: kartaBg, border: `1px solid ${border}`, opacity: t.minutky ? 1 : 0.7 }}>
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)', minWidth: 130 }}>{t.nazev}</span>
                            <span className="flex items-center gap-1.5 text-xs" style={{ color: muted }}>
                                <span style={{ width: 8, height: 8, borderRadius: 4, background: barvaStanice(staniceTerminalu(t)) }} />{nazevStanice(staniceTerminalu(t))}
                            </span>
                            <span className="text-xs tabular-nums" style={{ color: muted }}>{t.obrazovka ? t.obrazovka.replace('x', '×') : 'rozlišení zatím neohlášeno'}</span>
                            <span className="flex-1" />
                            <Prepinac zapnuto={!!t.minutky} onClick={() => onZmena(t, { minutky: !t.minutky })} barva={barva} muted={muted} isDark={isDark}>minutky</Prepinac>
                        </div>
                        {t.minutky && (
                            <div className="flex flex-wrap items-center gap-4">
                                <div className="flex rounded-lg p-0.5" style={{ border: `1px solid ${border}` }} role="radiogroup" aria-label="Velikost lišty">
                                    {VELIKOSTI.map(v => (
                                        <button key={v.id} type="button" role="radio" aria-checked={z.velikost === v.id} onClick={() => uloz({ velikost: v.id })}
                                            className="text-xs rounded-md px-2.5" style={{ minHeight: 30, border: 'none', cursor: 'pointer', fontWeight: z.velikost === v.id ? 700 : 400,
                                                background: z.velikost === v.id ? barva : 'none', color: z.velikost === v.id ? '#fff' : muted }}>
                                            {v.nazev}{v.id === 'auto' && auto ? ` → ${VELIKOSTI.find(x => x.id === auto).nazev.toLowerCase()}` : ''}
                                        </button>
                                    ))}
                                </div>
                                <label className="flex items-center gap-2 text-xs" style={{ color: muted }}>
                                    doladit
                                    <input type="range" min={80} max={150} step={5} value={z.meritko} onChange={e => uloz({ meritko: parseInt(e.target.value, 10) })} />
                                    <span className="font-bold tabular-nums" style={{ color: 'var(--text-primary)', minWidth: 38 }}>{z.meritko} %</span>
                                </label>
                                <Prepinac zapnuto={z.zvuk} onClick={() => uloz({ zvuk: !z.zvuk })} barva={barva} muted={muted} isDark={isDark}>zvuk</Prepinac>
                            </div>
                        )}
                    </div>
                );
            })}
            <p className="text-[11px]" style={{ color: muted }}>
                Auto podle šířky obrazovky: do 1600 px kompaktní (HD), do 2560 px standard (Full HD), víc velká (4K). „Doladit“ zvětší jen minutky, ne celou kuchyň.
            </p>
        </div>
    );
}
